import moment from 'moment-timezone'
import { ref, reactive, computed, watch } from 'vue'
import { useToast } from 'vue-toastification'

const DATE_KEY_FORMAT = 'YYYY-MM-DD'
const DATE_LABEL_FORMAT = 'MMM D, YYYY'

// How far ahead the default may search; the picker only offers 3 months.
const MAX_DEFAULT_LOOKAHEAD_DAYS = 90

const isWeekend = (date) => date.day() === 0 || date.day() === 6

/**
 * Whether a calendar day can be picked as the distribution date: a weekday
 * that is not one of the `YYYY-MM-DD` blackout dates.
 *
 * @param {import('moment').Moment|string} date moment, or a YYYY-MM-DD string
 * @param {string[]} blackoutDates
 */
export const isDistributionDateAvailable = (date, blackoutDates = []) => {
  const day = moment.isMoment(date) ? date : moment(date, DATE_KEY_FORMAT, true)

  return (
    day.isValid() &&
    !isWeekend(day) &&
    !blackoutDates.includes(day.format(DATE_KEY_FORMAT))
  )
}

/**
 * First available distribution date on or after `from`, as YYYY-MM-DD in
 * `from`'s timezone; null when none is found within the lookahead window.
 *
 * @param {import('moment').Moment} from
 * @param {string[]} blackoutDates
 */
export const getNextAvailableDistributionDate = (from, blackoutDates = []) => {
  const candidate = from.clone()

  for (let i = 0; i < MAX_DEFAULT_LOOKAHEAD_DAYS; i++) {
    if (isDistributionDateAvailable(candidate, blackoutDates)) {
      return candidate.format(DATE_KEY_FORMAT)
    }

    candidate.add(1, 'day')
  }

  return null
}

export const useTime = (
  onSelectDate,
  distributionDate,
  blackoutDates = ref([]),
  isBlackoutDatesReady = ref(true)
) => {
  const timeZone = 'America/New_York'
  const dateFormat = 'MM/DD/YYYY'
  const dateTimeFormat = 'MM/DD/YYYY h:mm a'
  const currentDate = moment.tz(moment(), timeZone)
  const currentDateString = currentDate.format(dateFormat)
  const currentDateTimeString = currentDate.format(dateTimeFormat)
  const currentDateTimestamp = currentDateTimeString.valueOf()
  const todayInNewYork = moment.tz('America/New_York').format('YYYY-MM-DD')

  const today = moment().tz(timeZone).format()
  const tomorrow = moment().tz(timeZone).add(1, 'day').format()

  const showBeforeThreePmNotificationModal = ref(false)
  const showBeforeMidnightNotificationModal = ref(false)

  let beforeThreePmNotification = {
    displayBetween: {
      start: `${currentDateString} 2:44:00 PM`,
      startTimestamp: moment(`${currentDateString} 2:44:00 PM`).valueOf(),
      end: `${currentDateString} 2:59:59 PM`,
      endTimestamp: moment(`${currentDateString} 2:59:59 PM`).valueOf(),
    },
  }

  let beforeMidnightNotification = {
    displayBetween: {
      start: `${currentDateString} 11:44:00 PM`,
      startTimestamp: moment(`${currentDateString} 11:44:00 PM`).valueOf(),
      end: `${currentDateString} 11:59:59 PM`,
      endTimestamp: moment(`${currentDateString} 11:59:59 PM`).valueOf(),
    },
  }

  const expired = moment(currentDateTimestamp).isAfter(
    beforeThreePmNotification.displayBetween.end
  )

  const leftTimeTicker = reactive({
    minutes: null,
    seconds: null,
    expired,
  })

  const minDatePickerDate = computed(() => {
    return leftTimeTicker.expired
      ? moment().tz(timeZone).add(1, 'day').subtract(6, 'hours').format()
      : today
  })

  const maxDatePickerDate = computed(() => {
    return moment().tz(timeZone).add(3, 'months').format()
  })

  const showBeforeThreePmNotification = computed(() => {
    return (
      moment(currentDateTimestamp).isAfter(
        beforeThreePmNotification.displayBetween.startTimestamp.valueOf()
      ) &&
      moment(beforeThreePmNotification.displayBetween.endTimestamp).isAfter(
        currentDateTimestamp
      )
    )
  })

  const showBeforeMidnightNotification = computed(() => {
    return (
      moment(currentDateTimestamp).isAfter(
        beforeMidnightNotification.displayBetween.startTimestamp.valueOf()
      ) &&
      moment(beforeMidnightNotification.displayBetween.endTimestamp).isAfter(
        currentDateTimestamp
      )
    )
  })

  function getLeftTime(leftTickTime, disableCurrentDate = false) {
    let tickInterval = null

    function createInterval() {
      tickInterval = setInterval(() => {
        leftTickTime -= 1000

        if (leftTickTime < 0) {
          if (disableCurrentDate) {
            leftTimeTicker.expired = true
            showBeforeThreePmNotificationModal.value = false
          }

          return clearInterval(tickInterval)
        }

        leftTimeTicker.minutes = Math.floor(
          (leftTickTime % (1000 * 60 * 60)) / (1000 * 60)
        )

        const seconds = Math.floor((leftTickTime % (1000 * 60)) / 1000)

        leftTimeTicker.seconds = seconds < 10 ? `0${seconds}` : seconds
      }, 1000)
    }

    createInterval()
  }

  const onHideBeforeMidnightModal = () => {
    showBeforeMidnightNotificationModal.value = false
  }

  const onHideBeforeThreePmNotification = () => {
    showBeforeThreePmNotificationModal.value = false
  }

  // Default to the next business day (ET) after today. Passed to onSelectDate
  // as a YYYY-MM-DD string, exactly like a calendar click, so the browser's
  // timezone cannot shift the day.
  const onSetDistributionDate = (onSaved) => {
    if (distributionDate.value) return

    const nextDate = getNextAvailableDistributionDate(
      moment().tz(timeZone).add(1, 'day'),
      blackoutDates.value
    )

    nextDate && onSelectDate(nextDate, onSaved)
  }

  // Earliest selectable day: today until the 3 p.m. ET cutoff, then tomorrow.
  const earliestDistributionDate = () =>
    moment()
      .tz(timeZone)
      .add(leftTimeTicker.expired ? 1 : 0, 'day')
      .format(DATE_KEY_FORMAT)

  // Blackout dates arrive asynchronously, and the calendar only enforces them
  // for clicks — not for a value set programmatically — so both the default
  // and any date restored from the order are only settled once the list is in
  // (and never auto-picked if it failed to load). A restored date that is no
  // longer selectable is dropped and re-defaulted.
  const toast = useToast()
  const dateLabel = (date) =>
    moment(date, DATE_KEY_FORMAT, true).format(DATE_LABEL_FORMAT)

  watch(
    isBlackoutDatesReady,
    (ready) => {
      if (!ready) return

      const stored = distributionDate.value
      const storedIsUnavailable =
        stored &&
        (stored < earliestDistributionDate() ||
          !isDistributionDateAvailable(stored, blackoutDates.value))

      if (storedIsUnavailable) distributionDate.value = null

      // Announce a replacement only once the server has accepted it.
      const announceMove = (movedTo) => {
        const movedToLabel = dateLabel(movedTo)

        toast.warning(
          `${dateLabel(stored)} is no longer available for distribution. ` +
            `Your release date has been moved to ${movedToLabel}; please confirm it.`
        )
      }

      onSetDistributionDate(storedIsUnavailable ? announceMove : undefined)
    },
    { immediate: true }
  )

  watch(
    showBeforeThreePmNotification,
    (value) => {
      if (!value) return

      showBeforeThreePmNotificationModal.value = true

      getLeftTime(
        beforeThreePmNotification.displayBetween.endTimestamp -
          moment(currentDateTimestamp).valueOf(),
        true
      )
    },
    { immediate: true }
  )

  watch(
    showBeforeMidnightNotification,
    (value) => {
      if (!value) return

      showBeforeMidnightNotificationModal.value = true

      getLeftTime(
        moment(
          beforeMidnightNotification.displayBetween.endTimestamp
        ).valueOf() - moment(currentDateTimestamp).valueOf()
      )
    },
    { immediate: true }
  )

  return {
    currentDate,
    currentDateString,
    currentDateTimeString,
    currentDateTimestamp,
    todayInNewYork,
    leftTimeTicker,
    minDatePickerDate,
    maxDatePickerDate,
    showBeforeThreePmNotificationModal,
    showBeforeMidnightNotificationModal,

    showBeforeThreePmNotification,
    showBeforeMidnightNotification,

    onHideBeforeThreePmNotification,
    onHideBeforeMidnightModal,
  }
}
